/**
 * Onboarding hooks for firm Q&A workflow
 *
 * These hooks provide TanStack Query integration for the onboarding
 * workflow, including fetching questions, submitting answers, and
 * tracking progress.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getOnboardingQuestions,
  submitOnboardingAnswer,
  getOnboardingProgress,
  type OnboardingQuestionFilters,
  type OnboardingQuestionsResponse,
  type OnboardingProgress,
  type SubmitAnswerRequest,
  type SubmitAnswerResponse,
} from '@/api/onboarding';

// -----------------------------------------------------------------------------
// Query Keys
// -----------------------------------------------------------------------------

export const onboardingQueryKeys = {
  all: ['onboarding'] as const,
  questions: (filters?: OnboardingQuestionFilters) =>
    [...onboardingQueryKeys.all, 'questions', filters] as const,
  progress: (firmId: number) =>
    [...onboardingQueryKeys.all, 'progress', firmId] as const,
};

// -----------------------------------------------------------------------------
// Query Hooks
// -----------------------------------------------------------------------------

/**
 * Hook to fetch unanswered onboarding questions for a firm
 */
export function useOnboardingQuestions(
  filters: OnboardingQuestionFilters,
  options?: { enabled?: boolean }
) {
  return useQuery<OnboardingQuestionsResponse, Error>({
    queryKey: onboardingQueryKeys.questions(filters),
    queryFn: () => getOnboardingQuestions(filters),
    staleTime: 30_000,
    enabled: filters.firm_id > 0 && (options?.enabled ?? true),
  });
}

/**
 * Hook to get onboarding progress for a firm
 */
export function useOnboardingProgress(
  firmId: number,
  options?: { enabled?: boolean }
) {
  return useQuery<OnboardingProgress, Error>({
    queryKey: onboardingQueryKeys.progress(firmId),
    queryFn: () => getOnboardingProgress(firmId),
    staleTime: 30_000,
    enabled: firmId > 0 && (options?.enabled ?? true),
  });
}

// -----------------------------------------------------------------------------
// Mutation Hooks
// -----------------------------------------------------------------------------

/**
 * Hook to submit an onboarding answer
 */
export function useSubmitAnswer() {
  const queryClient = useQueryClient();

  return useMutation<SubmitAnswerResponse, Error, SubmitAnswerRequest>({
    mutationFn: (request) => submitOnboardingAnswer(request),
    onSuccess: () => {
      // Invalidate questions list and progress
      queryClient.invalidateQueries({
        queryKey: onboardingQueryKeys.all,
      });
    },
  });
}

// -----------------------------------------------------------------------------
// Re-exports
// -----------------------------------------------------------------------------

export type {
  OnboardingQuestionFilters,
  OnboardingQuestionsResponse,
  OnboardingProgress,
  SubmitAnswerRequest,
  SubmitAnswerResponse,
} from '@/api/onboarding';

export type { OnboardingQuestion } from '@/api/onboarding';
